import React, { useCallback, useEffect, useRef, useState } from "react";
import "./HomePage.css";

const THEME_STORAGE_KEY = "portfolio-theme";

function readStoredTheme() {
  if (typeof window === "undefined") return "light";
  return window.localStorage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
}

const SKILLS = [
  "Python",
  "FastAPI",
  "Django",
  "Flask",
  "Sanic",
  "HTML",
  "CSS",
  "JavaScript",
  "TypeScript",
  "React",
  "NestJS",
  "Next.js",
  "Flutter",
  "Git",
  "Linux",
  "Docker",
  "Kubernetes (basic)",
  "AWS Services",
  "Azure",
  "Hetzner",
  "Ansible",
  "GitLab CI",
  "GitHub Actions",
  "Bitbucket Pipelines",
  "Azure DevOps",
  "Nginx",
  "Apache2",
  "HAProxy",
  "FreeSWITCH",
  "MySQL",
  "PostgreSQL",
  "MongoDB",
  "Redis",
  "Kafka",
  "MQTT",
  "Minio",
  "Supabase",
  "ELK Stack",
  "OpenSearch",
  "Zabbix",
  "Sentry",
  "Datadog",
  "Grafana",
  "New Relic",
];

const CASE_STUDIES = [
  {
    title: "Multi-tenant realtime platform",
    chaos:
      "Weak ownership between squads, outages during peak load, and a roadmap that could not keep pace with enterprise commitments.",
    intervention:
      "Reset architecture boundaries, tightened SLOs, and put delivery on a weekly release train with clear rollback paths.",
    result:
      "Platform scaled to tens of thousands of concurrent sessions with predictable latency and fewer customer escalations.",
  },
  {
    title: "High-throughput logistics stack",
    chaos:
      "Services stepping on each other at peak, opaque failures, and releases that required manual heroics.",
    intervention:
      "Clarified domain ownership, hardened Kafka-driven flows, and partnered with senior ICs on zero-downtime release patterns.",
    result:
      "Pipelines sustained 1M+ orders per day class load with clearer failure domains and calmer on-call rotations.",
  },
  {
    title: "Delivery and operations reset",
    chaos:
      "Frequent production regressions, slow feedback from CI, and engineers unsure what “done” meant before merge.",
    intervention:
      "Introduced Docker-based production CI/CD, gated merges on integration checks, and standardized post-incident reviews.",
    result:
      "Roughly 60% fewer deployment-related failures within two quarters and faster recovery when issues slipped through.",
  },
  {
    title: "Cost and platform efficiency",
    chaos:
      "Static fleets and manual runbooks burning budget, plus little visibility into who owned each workload.",
    intervention:
      "Moved suitable workloads to serverless patterns, automated recurring tasks, and documented ownership per service.",
    result:
      "About 40% infrastructure cost reduction on targeted systems and a clearer map of spend versus value.",
  },
];

const PLAYBOOK = [
  {
    title: "Process design",
    body:
      "Delivery cadence, definition of done, incident response, and governance that fits a startup stage instead of copying a big-tech handbook.",
  },
  {
    title: "Team mentorship",
    body:
      "Coaching ICs and leads, raising the bar on reviews, and building psychological safety so hard problems get surfaced early.",
  },
  {
    title: "Technical architecture",
    body:
      "Service boundaries, data paths, observability, and cost-aware infrastructure so the product can grow without rewriting every year.",
  },
  {
    title: "Advisory & freelance delivery",
    body:
      "Architecture reviews and design tradeoffs when you need expert judgment without a full re-org. Freelance team lead or senior IC execution when you need ownership on the floor. Cleanup and hardening when rapid or AI-assisted development left you with a fragile base: tests, boundaries, and releases you can trust.",
  },
];

const NAV_LINKS = [
  { id: "positioning", label: "Focus" },
  { id: "case-studies", label: "Turnarounds" },
  { id: "playbook", label: "Playbook" },
  { id: "leadership", label: "Leadership" },
  { id: "skills", label: "Skills" },
  { id: "connect", label: "Contact" },
];

const LEADERSHIP_MILESTONES = [
  {
    role: "Software Engineer",
    period: "Individual contributor",
    summary:
      "Shipped production services end to end, owned databases and deployments, and learned how systems fail in the real world.",
  },
  {
    role: "Software Engineer II",
    period: "Senior IC",
    summary:
      "Led lifecycle work from design through operations, cut deployment risk with stronger automation, and mentored a widening circle of engineers.",
  },
  {
    role: "Team Lead",
    period: "People and delivery",
    summary:
      "Ran allocation and execution across products, partnered with leadership on priorities, and drove architecture for complex multi-tenant systems.",
  },
  {
    role: "Head of Engineering",
    period: "Organization scope",
    summary:
      "Set direction across teams, raised hiring and quality standards, and aligned engineering outcomes with business constraints without losing velocity.",
  },
];

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (typeof window === "undefined" || !window.IntersectionObserver) {
      setVisible(true);
      return undefined;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return [ref, visible];
}

function RevealSection({ children, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className={`reveal ${visible ? "reveal--visible" : ""} ${className}`.trim()}>
      {children}
    </div>
  );
}

function IconMail() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <path d="M4 6h16v12H4V6z" strokeLinejoin="round" />
      <path d="M4 7l8 6 8-6" strokeLinecap="round" />
    </svg>
  );
}

function IconGitHub() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
    </svg>
  );
}

function IconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

function IconSun() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path
        strokeLinecap="round"
        d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
      />
    </svg>
  );
}

function IconMoon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
      />
    </svg>
  );
}

const COMPACT_HEADER_MQ = "(max-width: 1023px)";

export default function HomePage() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [compactHeader, setCompactHeader] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(COMPACT_HEADER_MQ).matches : false
  );
  const [theme, setTheme] = useState(readStoredTheme);

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_HEADER_MQ);
    const sync = () => setCompactHeader(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!compactHeader) setNavMenuOpen(false);
  }, [compactHeader]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore quota / private mode */
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "light" ? "dark" : "light"));
  }, []);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToId = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const closeNavMenu = useCallback(() => setNavMenuOpen(false), []);

  const navLinkClick = useCallback(
    (e, id) => {
      e.preventDefault();
      if (compactHeader) closeNavMenu();
      scrollToId(id);
    },
    [compactHeader, closeNavMenu, scrollToId]
  );

  useEffect(() => {
    if (!navMenuOpen || !compactHeader) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeNavMenu();
    };
    window.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [navMenuOpen, compactHeader, closeNavMenu]);

  return (
    <div className="page">
      <div className="page__bg" aria-hidden="true" />
      {navMenuOpen && compactHeader ? (
        <button
          type="button"
          className="nav-mobile-overlay"
          aria-hidden="true"
          tabIndex={-1}
          onClick={closeNavMenu}
        />
      ) : null}
      <nav
        className={`nav ${navScrolled ? "nav--scrolled" : ""} ${compactHeader && navMenuOpen ? "nav--menu-open" : ""}`}
        aria-label="Site"
      >
        <span className="nav__brand">Balu M N</span>
        <div
          className={`nav__links${compactHeader && navMenuOpen ? " nav__links--open" : ""}`}
          id="site-nav-links"
          aria-hidden={compactHeader ? !navMenuOpen : false}
        >
          {NAV_LINKS.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              tabIndex={compactHeader && !navMenuOpen ? -1 : undefined}
              onClick={(e) => navLinkClick(e, id)}
            >
              {label}
            </a>
          ))}
        </div>
        <div className="nav__controls">
          {compactHeader ? (
            <button
              type="button"
              className="nav__menu-toggle"
              onClick={() => setNavMenuOpen((open) => !open)}
              aria-expanded={navMenuOpen}
              aria-controls="site-nav-links"
              aria-label={navMenuOpen ? "Close site menu" : "Open site menu"}
            >
              <span className="nav__menu-toggle-box" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          ) : null}
          <button
            type="button"
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
            title={theme === "light" ? "Dark mode" : "Light mode"}
          >
            {theme === "light" ? <IconMoon /> : <IconSun />}
          </button>
        </div>
      </nav>

      <main className="page__content">
        <header className="hero" id="top">
          <p className="hero__eyebrow">
            Engineering turnaround · Fractional CTO · Architecture advisory · Freelance developer · Kochi,
            India
          </p>
          <h1 className="hero__title">I build the teams that build your product.</h1>
          <p className="hero__lead">
            Startups bring me in when engineering is underwater: unclear ownership, brittle releases, and debt that
            compounds faster than features. I have led four full revamps from software engineer through team lead and
            head of engineering scope. I do not only write code. I build the culture, processes, and people that ship
            the product.
          </p>
          <div className="hero__actions">
            <button type="button" className="btn btn--primary" onClick={() => scrollToId("connect")}>
              Start a conversation
            </button>
            <a className="btn btn--ghost" href="https://github.com/balumn" target="_blank" rel="noopener noreferrer">
              GitHub
            </a>
          </div>
          <p className="hero__sub hero__sub--intro">
            Hire me for org turnarounds, system design opinions that survive production, freelance team lead or senior dev
            work, and turning vibe-coded or prototype sprawl into something your team can own.
          </p>
          <p className="hero__sub hero__sub--muted">
            Below: how engagements tend to unfold, the playbook, and a straightforward way to reach me.
          </p>
        </header>

        <section className="section" id="positioning" aria-labelledby="positioning-heading">
          <RevealSection>
            <p className="section__label">The problem I solve</p>
            <h2 id="positioning-heading" className="section__title">
              From chaos to a team that executes
            </h2>
            <p className="section__intro">
              Weak teams, noisy processes, and technical debt show up as missed dates, angry customers, and attrition. I
              step in, stabilize delivery, and install the habits that keep quality high after I step back. You get an
              operator who has done this before, not a slide deck. Same person can advise on architecture, lead squads
              as a freelance TL, ship code as a senior IC, or scrub a messy MVP into release shape. Pick the mix your
              stage needs.
            </p>
          </RevealSection>
        </section>

        <section className="section" id="case-studies" aria-labelledby="case-studies-heading">
          <RevealSection>
            <p className="section__label">Turnaround case studies</p>
            <h2 id="case-studies-heading" className="section__title">
              Four startup revamps, same playbook
            </h2>
            <p className="section__intro">
              Each engagement is different, but the narrative repeats: chaos, intervention, measurable result. These
              sketches summarize patterns I have seen and helped deliver. Specifics always depend on your team and
              context.
            </p>
            <div className="case-grid">
              {CASE_STUDIES.map((c) => (
                <article key={c.title} className="case-card">
                  <h3 className="case-card__title">{c.title}</h3>
                  <div className="case-block">
                    <p className="case-block__label">The chaos</p>
                    <p className="case-block__text">{c.chaos}</p>
                  </div>
                  <div className="case-block">
                    <p className="case-block__label">The intervention</p>
                    <p className="case-block__text">{c.intervention}</p>
                  </div>
                  <div className="case-block case-block--result">
                    <p className="case-block__label">The result</p>
                    <p className="case-block__text">{c.result}</p>
                  </div>
                </article>
              ))}
            </div>
          </RevealSection>
        </section>

        <section className="section" id="playbook" aria-labelledby="playbook-heading">
          <RevealSection>
            <p className="section__label">Services / approach</p>
            <h2 id="playbook-heading" className="section__title">
              Engineering playbook
            </h2>
            <p className="section__intro">
              This is how I work when you need velocity and safety at the same time. Engagements can be fractional
              leadership, architecture advice on a retainer, freelance team lead or developer capacity, a focused rescue,
              or a defined transformation program, including cleanup when fast iteration or AI-assisted coding outpaced
              discipline.
            </p>
            <div className="playbook-grid">
              {PLAYBOOK.map((item) => (
                <article key={item.title} className="playbook-card">
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </RevealSection>
        </section>

        <section className="section" id="leadership" aria-labelledby="leadership-heading">
          <RevealSection>
            <p className="section__label">Proof of leadership</p>
            <h2 id="leadership-heading" className="section__title">
              Progression matters more than any single title
            </h2>
            <p className="section__intro">
              Titles shift by company size. What stayed constant is scope: broader ownership, calmer delivery, and teams
              that outlast any one project.
            </p>
            <ol className="timeline">
              {LEADERSHIP_MILESTONES.map((m, i) => (
                <li key={m.role} className="timeline__item">
                  <div className="timeline__marker" aria-hidden="true">
                    <span className="timeline__dot" />
                    {i < LEADERSHIP_MILESTONES.length - 1 && <span className="timeline__line" />}
                  </div>
                  <div className="timeline__body">
                    <p className="timeline__role">{m.role}</p>
                    <p className="timeline__period">{m.period}</p>
                    <p className="timeline__summary">{m.summary}</p>
                  </div>
                </li>
              ))}
            </ol>
          </RevealSection>
        </section>

        <section className="section" id="skills" aria-labelledby="skills-heading">
          <RevealSection>
            <p className="section__label">Technical depth</p>
            <h2 id="skills-heading" className="section__title">
              Stack and platforms
            </h2>
            <p className="section__intro">
              Leadership comes first on this site. These are tools I still use hands on when the problem requires it.
            </p>
            <div className="skills" role="list">
              {SKILLS.map((s, i) => (
                <span
                  key={s}
                  className="skill-pill"
                  role="listitem"
                  style={{ "--skill-stagger": i }}
                >
                  {s}
                </span>
              ))}
            </div>
          </RevealSection>
        </section>

        <section className="section" id="connect" aria-labelledby="connect-heading">
          <RevealSection>
            <div className="connect">
              <h2 id="connect-heading">Contact</h2>
              <p>
                For fractional CTO work, architecture and system-design advisory, freelance engagements, turnaround leadership, “vibe coded” base cleanup, or a confidential conversation
                about your engineering org, email is the best way to reach me.
              </p>
              <div className="social-row">
                <a className="social-link" href="mailto:balumnandan@gmail.com">
                  <IconMail />
                  Email
                </a>
                <a
                  className="social-link"
                  href="https://github.com/balumn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconGitHub />
                  GitHub
                </a>
                <a
                  className="social-link"
                  href="https://www.linkedin.com/in/balumn/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconLinkedIn />
                  LinkedIn
                </a>
              </div>
            </div>
          </RevealSection>
        </section>

        <footer className="footer">
          <p className="footer__row">
            <span className="footer__love">
              <IconHeart /> Made with love
            </span>
            <span className="footer__sep" aria-hidden="true">
              ·
            </span>
            <span className="footer__byline">Balu M N, Kochi, India</span>
          </p>
        </footer>
      </main>
    </div>
  );
}
